import { useEffect, useRef } from 'react';
import { getSyncedChartScrollLeft } from './useSyncedChartScroll';

/**
 * Open a horizontally scrollable chart on its most recent data instead of its
 * oldest.
 *
 * These charts render oldest-to-newest inside an `overflow-x: auto` viewport,
 * which mounts at `scrollLeft = 0` — so a chart covering a full year opened on
 * weeks nobody is looking at, and the current weeks were off-screen until the
 * user scrolled. This scrolls to the end once, on the first render where there
 * is actually something to scroll.
 *
 * It defers to two things rather than fighting them:
 * - a position already recorded for the sync group (`syncGroupId`), which means
 *   the user has scrolled somewhere in this session and should keep it;
 * - any scroll the user makes before the content finishes sizing.
 *
 * The scroll is applied by assignment, so the sync hook's own `scroll` listener
 * broadcasts it and every chart in the group opens on the same weeks.
 *
 * @param {React.RefObject<HTMLElement>} scrollContainerRef — the scrolling viewport
 * @param {object}  [options]
 * @param {boolean} [options.enabled=true] — false while data is still loading
 * @param {string}  [options.syncGroupId] — group to check for an existing position
 * @param {unknown} [options.resetKey] — re-arms the one-shot when it changes (e.g. commodity)
 */
export function useInitialScrollToLatest(scrollContainerRef, options = {}) {
  const { enabled = true, syncGroupId, resetKey } = options;
  const appliedForRef = useRef(Symbol('unset'));

  useEffect(() => {
    if (!enabled) return;
    if (appliedForRef.current === resetKey) return;

    let rafId = 0;
    let frames = 0;
    let cancelled = false;

    // Width comes from the chart's own measurement, so the content is often
    // still narrower than the viewport for the first frame or two.
    const MAX_FRAMES = 60;

    const tick = () => {
      if (cancelled) return;

      const el = scrollContainerRef?.current;
      if (el) {
        // The user already has a position in this group — leave it alone.
        if (getSyncedChartScrollLeft(syncGroupId) !== null) {
          appliedForRef.current = resetKey;
          return;
        }
        // The user scrolled while we were waiting for the content to size.
        if (el.scrollLeft > 0) {
          appliedForRef.current = resetKey;
          return;
        }

        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll > 1) {
          el.scrollLeft = maxScroll;
          appliedForRef.current = resetKey;
          return;
        }
      }

      if (++frames >= MAX_FRAMES) return;
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [scrollContainerRef, enabled, syncGroupId, resetKey]);
}
